import type { FC } from 'react';
import { useRef, useCallback } from 'react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
}

interface ToolbarAction {
  label: string;
  ariaLabel: string;
  apply: (value: string, start: number, end: number) => { newValue: string; cursorPos: number };
}

const toolbarActions: ToolbarAction[] = [
  {
    label: 'B',
    ariaLabel: '太字',
    apply: (value, start, end) => {
      const selected = value.slice(start, end);
      if (selected) {
        const newValue = value.slice(0, start) + `**${selected}**` + value.slice(end);
        return { newValue, cursorPos: end + 4 };
      }
      const placeholder = 'テキスト';
      const newValue = value.slice(0, start) + `**${placeholder}**` + value.slice(end);
      return { newValue, cursorPos: start + 2 + placeholder.length };
    },
  },
  {
    label: 'I',
    ariaLabel: 'イタリック',
    apply: (value, start, end) => {
      const selected = value.slice(start, end);
      if (selected) {
        const newValue = value.slice(0, start) + `*${selected}*` + value.slice(end);
        return { newValue, cursorPos: end + 2 };
      }
      const placeholder = 'テキスト';
      const newValue = value.slice(0, start) + `*${placeholder}*` + value.slice(end);
      return { newValue, cursorPos: start + 1 + placeholder.length };
    },
  },
  {
    label: '🔗',
    ariaLabel: 'リンクを挿入',
    apply: (value, start, end) => {
      const selected = value.slice(start, end);
      if (selected) {
        const newValue = value.slice(0, start) + `[${selected}](url)` + value.slice(end);
        return { newValue, cursorPos: start + selected.length + 3 };
      }
      const newValue = value.slice(0, start) + '[テキスト](url)' + value.slice(end);
      return { newValue, cursorPos: start + 8 };
    },
  },
  {
    label: '•',
    ariaLabel: '箇条書きリスト',
    apply: (value, start, end) => {
      const selected = value.slice(start, end);
      if (selected) {
        const lines = selected.split('\n');
        const prefixed = lines.map((line) => `- ${line}`).join('\n');
        const newValue = value.slice(0, start) + prefixed + value.slice(end);
        return { newValue, cursorPos: start + prefixed.length };
      }
      const newValue = value.slice(0, start) + '- ' + value.slice(end);
      return { newValue, cursorPos: start + 2 };
    },
  },
  {
    label: '1.',
    ariaLabel: '番号付きリスト',
    apply: (value, start, end) => {
      const selected = value.slice(start, end);
      if (selected) {
        const lines = selected.split('\n');
        const prefixed = lines.map((line, i) => `${i + 1}. ${line}`).join('\n');
        const newValue = value.slice(0, start) + prefixed + value.slice(end);
        return { newValue, cursorPos: start + prefixed.length };
      }
      const newValue = value.slice(0, start) + '1. ' + value.slice(end);
      return { newValue, cursorPos: start + 3 };
    },
  },
];

export const MarkdownEditor: FC<MarkdownEditorProps> = ({
  value,
  onChange,
  rows = 6,
  placeholder,
  required,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleToolbarClick = useCallback(
    (action: ToolbarAction) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const { newValue, cursorPos } = action.apply(value, start, end);

      onChange(newValue);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [value, onChange],
  );

  return (
    <div>
      <div className="flex gap-1 rounded-t-lg border border-slate-300 bg-slate-50 px-2 py-1.5">
        {toolbarActions.map((action) => (
          <button
            key={action.ariaLabel}
            type="button"
            title={action.ariaLabel}
            aria-label={action.ariaLabel}
            onClick={() => handleToolbarClick(action)}
            className="flex h-7 w-7 items-center justify-center rounded text-sm text-slate-600 hover:bg-slate-200"
          >
            {action.label === 'B' ? (
              <span className="font-bold">B</span>
            ) : action.label === 'I' ? (
              <span className="italic">I</span>
            ) : (
              <span>{action.label}</span>
            )}
          </button>
        ))}
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-b-lg rounded-t-none border border-t-0 border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
};
